// Topbar avatar dropdown showing the signed-in user's email + role +
// Sign out. Replaces the older static initial-circle avatar AND the
// sidebar Sign-out row.
//
// Pattern lifted verbatim from Utility Desk's PersonaAvatar (mousedown
// click-outside + Escape closes) — see
// /home/claude/claude_code/projects/UtilityDesk/src/components/TopBar.tsx:183-267.
//
// Sign-out behavior: POST /auth/logout (clears Supabase session cookies)
// then window.location.replace("/") for a hard reload. The hard reload
// is required because the worker's refresh middleware races with cookie
// deletion and could re-mint a session from the still-present refresh
// token if we left it to React Query invalidation. See
// feedback_logout_hard_reload_pattern.md.

import { useEffect, useRef, useState } from 'react';
import { LogOut } from 'lucide-react';
import type { MeResponse } from '../api/client.js';
import { Badge } from './ui/badge.js';
import { cn } from '../lib/utils.js';

export function UserMenu({ me }: { me: MeResponse }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const label = me.displayName ?? me.email;
  const initial = label.charAt(0).toUpperCase();
  const tenantName = me.tenant?.name ?? '';

  const handleSignOut = async () => {
    setOpen(false);
    try {
      await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      // Hard reload — bypasses any in-flight queries that might re-mint
      // a session from a still-present refresh cookie.
      window.location.replace('/');
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={me.email}
        className="flex items-center gap-2 rounded-md py-1 pl-1 pr-2.5 transition-colors hover:bg-white/15"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
          {initial}
        </span>
        <span className="hidden text-xs font-medium text-white/90 sm:inline">
          {label}
        </span>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-md border border-border bg-card text-foreground shadow-xl">
          <div className="border-b border-border px-3 py-3">
            {tenantName && (
              <div className="truncate text-sm font-semibold text-foreground">
                {tenantName}
              </div>
            )}
            {me.displayName && (
              <div className="truncate text-xs text-foreground">{me.displayName}</div>
            )}
            <div className="truncate text-xs text-muted-foreground">{me.email}</div>
            {me.role && (
              <div className="mt-1.5">
                <Badge variant={me.role === 'owner' ? 'default' : 'secondary'}>
                  {me.role.replace('_', ' ')}
                </Badge>
                {me.env === 'dev' && (
                  <span className="ml-2 text-[0.625rem] uppercase tracking-wider text-taupe">
                    dev
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className={cn(
              'flex w-full items-center gap-3 border-t border-border px-3 py-2.5 text-left text-sm font-semibold text-muted-foreground transition-colors',
              'hover:bg-destructive/10 hover:text-destructive',
            )}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
              <LogOut className="h-3.5 w-3.5" />
            </div>
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
